import { Query } from '@varo/graphql-schema-types';
import { useFocusEffect } from 'expo-router';
import {
  SavingsRates,
  toSavingsRates,
} from 'features/accounts/helpers/savingsRates.helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import GetSavingsRateInfo from 'services/graphql/queries/GetSavingsRateInfo';
import { useAppLifecycle } from 'shared/hooks/useAppLifecycle/useAppLifecycle';
import { CombinedError, useQuery } from 'urql';

type SavingsRateInfoData = Pick<Query, 'savingsRateInfo'>;

interface RatesSnapshot {
  data: SavingsRateInfoData | undefined;
  rates: SavingsRates | undefined;
}

interface UseSavingsRateInfoResult {
  rates: SavingsRates | undefined;
  fetching: boolean;
  isUnavailable: boolean;
  error: CombinedError | undefined;
  refetch: () => void;
}

export const useSavingsRateInfo = (): UseSavingsRateInfoResult => {
  const [{ data, fetching, error }, reexecuteQuery] =
    useQuery<SavingsRateInfoData>({
      query: GetSavingsRateInfo,
      requestPolicy: 'network-only',
    });

  const refetch = () => reexecuteQuery();

  const hasFocusedOnce = useRef(false);
  useFocusEffect(
    // useFocusEffect re-runs whenever the callback identity changes, so an unstable callback refetches on every render.
    // eslint-disable-next-line custom/no-manual-react-memoization
    useCallback(() => {
      if (hasFocusedOnce.current) {
        reexecuteQuery();
      }
      hasFocusedOnce.current = true;
    }, [reexecuteQuery]),
  );

  const { lastResumeAt } = useAppLifecycle();
  useEffect(() => {
    if (lastResumeAt) {
      reexecuteQuery();
    }
  }, [lastResumeAt, reexecuteQuery]);

  const [ratesSnapshot, setRatesSnapshot] = useState<RatesSnapshot>();
  if (data !== ratesSnapshot?.data) {
    setRatesSnapshot({
      data,
      rates:
        toSavingsRates(data?.savingsRateInfo, new Date()) ??
        (error ? ratesSnapshot?.rates : undefined),
    });
  }
  const rates = ratesSnapshot?.rates;

  return {
    rates,
    fetching,
    isUnavailable: !fetching && !rates,
    error,
    refetch,
  };
};
